import { eventAction } from "../services/view-context-history-state.service";
import { ActivatedRouteSnapshot } from "@angular/router";
import { ViewContextHistoryStateService } from "../services/view-context-history-state.service";
import {
  describeApiRequestFailed as buildApiFailedDescription,
  describeApiRequestStarted as buildApiStartedDescription,
  describeApiRequestSucceeded as buildApiSucceededDescription,
  extractSupportErrorMessage,
  resolveSupportContextError,
} from "./support-event-descriptions.helper";
import {
  catchError,
  EMPTY,
  Observable,
  OperatorFunction,
  throwError,
} from "rxjs";

export { extractSupportErrorMessage, resolveSupportContextError } from "./support-event-descriptions.helper";

/** Options when recording support history events. */
export interface SupportHistoryRecordOptions {
  /** Raw or parsed backend error for failure events. */
  error?: unknown;
}

/** Records support history entries from components, modals, or shared helpers. */
export type SupportHistoryRecorder = (
  eventAction: eventAction,
  eventDescription: string,
  options?: SupportHistoryRecordOptions,
) => void;

export interface CatchApiErrorOptions<T> {
  kind?: "mutation" | "fetch";
  context?: Record<string, string | number | null | undefined>;
  /** Observable emitted after recording the failure. Defaults to EMPTY. */
  fallback?: Observable<T>;
  /** Re-throw after recording instead of using fallback/EMPTY. */
  rethrow?: boolean;
  /** Optional side effect after the failure is recorded (e.g. console.error). */
  onError?: (error: unknown) => void;
}

/**
 * RxJS operator for API catchError handlers inside a support context.
 * Always records `action_failed` or `data_fetch_failed` before handling the error.
 */
export function catchApiError<T>(
  record: SupportHistoryRecorder | undefined,
  operation: string,
  options: CatchApiErrorOptions<T> = {},
): OperatorFunction<T, T> {
  return catchError((error: unknown) => {
    recordApiRequestFailed(
      record,
      operation,
      error,
      options.kind ?? "mutation",
      options.context,
    );
    options.onError?.(error);
    if (options.rethrow) {
      return throwError(() => error);
    }
    return options.fallback ?? (EMPTY as Observable<T>);
  });
}

/** Before an API call — records intent and payload context for support. */
export function recordApiRequestStarted(
  record: SupportHistoryRecorder | undefined,
  operation: string,
  context?: Record<string, string | number | null | undefined>,
): void {
  record?.(
    "api_request_started",
    buildApiStartedDescription(operation, context),
  );
}

/** After a successful API response. */
export function recordApiRequestSucceeded(
  record: SupportHistoryRecorder | undefined,
  operation: string,
  kind: "mutation" | "load" = "mutation",
  context?: Record<string, string | number | null | undefined>,
): void {
  if (!record) {
    return;
  }
  record(
    kind === "load" ? "list_loaded" : "action_succeeded",
    buildApiSucceededDescription(operation, context),
  );
}

/** Builds createContext params with resolved `error` for failure events. */
export function createSupportHistoryContext<TState>(
  svc: ViewContextHistoryStateService<TState>,
  input: {
    titleView: string;
    eventAction: eventAction;
    eventDescription: string;
    thisState: TState;
    route: ActivatedRouteSnapshot;
    options?: SupportHistoryRecordOptions;
    preserveHistory?: boolean;
  },
): void {
  void svc.createContext({
    titleView: input.titleView,
    eventAction: input.eventAction,
    eventDescription: input.eventDescription,
    thisState: input.thisState,
    route: input.route,
    error: resolveSupportContextError(input.eventAction, input.options?.error),
    preserveHistory: input.preserveHistory,
    setAutomaticallyScreenshot: false,
  });
}

/** After a failed API response. */
export function recordApiRequestFailed(
  record: SupportHistoryRecorder | undefined,
  operation: string,
  error: unknown,
  kind: "mutation" | "fetch" = "mutation",
  context?: Record<string, string | number | null | undefined>,
): void {
  if (!record) {
    return;
  }
  const errorMessage = extractSupportErrorMessage(error);
  const eventAction =
    kind === "fetch" ? "data_fetch_failed" : "action_failed";
  record(eventAction, buildApiFailedDescription(operation, errorMessage, context), {
    error,
  });
}
